/**
 * Ceiling 3D Viewer Component
 *
 * Shows a single ceiling surface with board-level rendering.
 * Uses the same shared modules as Room3DViewer for consistency.
 */

import { useTranslation } from 'react-i18next';
import React, { useEffect, useRef, useMemo, useCallback } from 'react';
import * as THREE from 'three';
import { RotateCcw, Maximize, Minimize } from 'lucide-react';

import { createScene, addLighting, addViewerLighting, configureRenderer } from './room3d/sceneBuilder';
import { registerLight, unregisterLight, LightingRegistryEntry } from '../dev/lightingRegistry';
import { createBoardMaterial } from './room3d/materialFactory';
import { generateCeilingBoardLayout } from './room3d/boardLayoutEngine';
import { formatDimension, createLabelSprite } from './room3d/dimensionLabels';
import {
  DEFAULT_FOV,
  applyDragRotation,
  applyZoom,
  interpolateState,
  calculateZoomToFit,
} from './room3d/cameraController';
import { attachTouchControls } from './room3d/touchController';
import { withViewerBoundary } from './Viewer3DBoundary';
import { exitFullscreenSafe, fullscreenSupported, requestFullscreenSafe } from '../../utils/fullscreen';
import {
  registerVisualizer,
  unregisterVisualizer,
  generateVisualizerId,
  VisualizerDebugEntry,
} from './room3d/visualizerDebug';

// Looking up at the ceiling from below: negative x = camera below, looking up
const CEILING_INITIAL_ROTATION = { x: -0.25, y: 4.0 };

import './Room3DViewer.css';

interface Ceiling3DViewerProps {
  length: number;
  width: number;
  color?: 'White' | 'Gray';
  compact?: boolean;
}

const Ceiling3DViewerBase: React.FC<Ceiling3DViewerProps> = ({
  length,
  width,
  color = 'White',
  compact = false,
}) => {
  const { t } = useTranslation('common');
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const groupRef = useRef<THREE.Group | null>(null);
  const isDraggingRef = useRef(false);
  const previousMouseRef = useRef({ x: 0, y: 0 });
  const rotationRef = useRef({ ...CEILING_INITIAL_ROTATION });
  const orbitDistanceRef = useRef(20);
  const centerRef = useRef(new THREE.Vector3(0, 0, 0));
  const animationFrameRef = useRef<number | null>(null);
  const spinnerRef = useRef<HTMLDivElement>(null);
  const lightingEntryRef = useRef<LightingRegistryEntry | null>(null);
  const debugEntryRef = useRef<VisualizerDebugEntry | null>(null);

  const [isAnimating, setIsAnimating] = React.useState(false);
  const [hasRotated, setHasRotated] = React.useState(false);
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [sceneReady, setSceneReady] = React.useState(false);
  const viewerWrapperRef = useRef<HTMLDivElement>(null);

  // iPhone Safari has no Element.requestFullscreen — the guarded helpers never
  // call what isn't there, and the button is hidden.
  const canFullscreen = fullscreenSupported();
  const toggleFullscreen = useCallback(() => {
    if (!viewerWrapperRef.current) return;
    if (!document.fullscreenElement) {
      void requestFullscreenSafe(viewerWrapperRef.current).then((ok) => { if (ok) setIsFullscreen(true); });
    } else {
      void exitFullscreenSafe().then((ok) => { if (ok) setIsFullscreen(false); });
    }
  }, []);

  useEffect(() => {
    const handler = () => {
      const fs = !!document.fullscreenElement;
      setIsFullscreen(fs);
      setTimeout(() => {
        const container = containerRef.current;
        if (container && cameraRef.current && rendererRef.current) {
          cameraRef.current.aspect = container.clientWidth / container.clientHeight;
          cameraRef.current.updateProjectionMatrix();
          rendererRef.current.setSize(container.clientWidth, container.clientHeight);
          renderScene();
        }
      }, 100);
    };
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // Generate ceiling board layout
  const boardLayout = useMemo(() => {
    return generateCeilingBoardLayout(length, width, color);
  }, [length, width, color]);

  const renderScene = () => {
    if (!cameraRef.current || !rendererRef.current || !sceneRef.current) return;
    const r = rotationRef.current;
    const d = orbitDistanceRef.current;
    const c = centerRef.current;
    cameraRef.current.position.set(
      d * Math.sin(r.y) * Math.cos(r.x) + c.x,
      d * Math.sin(r.x) + c.y,
      d * Math.cos(r.y) * Math.cos(r.x) + c.z
    );
    cameraRef.current.lookAt(c);
    cameraRef.current.updateProjectionMatrix();
    rendererRef.current.render(sceneRef.current, cameraRef.current);
  };

  useEffect(() => {
    if (!containerRef.current) return;

    // Instantly show spinner
    if (spinnerRef.current) {
      spinnerRef.current.style.transition = 'none';
      spinnerRef.current.style.opacity = '1';
      spinnerRef.current.style.pointerEvents = 'auto';
    }
    setSceneReady(false);

    const container = containerRef.current;

    // Scene + lighting (shared)
    const scene = createScene();
    sceneRef.current = scene;
    addLighting(scene, true); // flipY — lights from below for ceiling view

    // Camera
    const aspect = container.clientWidth / container.clientHeight;
    const camera = new THREE.PerspectiveCamera(50, aspect, 0.1, 500);
    centerRef.current.set(length / 2, 0, width / 2);
    cameraRef.current = camera;

    // Renderer (shared config, shadows enabled)
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    configureRenderer(renderer, true);
    renderer.setSize(container.clientWidth, container.clientHeight);
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Build ceiling
    const group = new THREE.Group();
    groupRef.current = group;
    scene.add(group);
    buildCeiling(group);

    // Add interior lighting + envMap (ceiling mode: light below surface)
    // Use a nominal height based on the smaller dimension for light distance
    const nominalHeight = Math.min(length, width) * 0.5;
    const readyTimeout = setTimeout(() => {
      renderScene();
      setSceneReady(true);
    }, 2000);

    let hemiLight: THREE.HemisphereLight | null = null;
    scene.traverse(child => {
      if (child instanceof THREE.HemisphereLight) hemiLight = child;
    });

    addViewerLighting(scene, group, renderer, 'ceiling',
      { length, width, height: nominalHeight },
      (light, setup) => {
        clearTimeout(readyTimeout);
        const entry: LightingRegistryEntry = {
          viewerType: 'ceiling',
          light,
          hemiLight,
          probe: setup.probe,
          envMap: setup.envMap,
          floorMesh: null,
          renderer,
          scene,
          roomGroup: group,
          renderScene,
          getRotation: () => ({ ...rotationRef.current }),
          roomDimensions: { length, width, height: nominalHeight },
        };
        lightingEntryRef.current = entry;
        registerLight(entry);
        requestAnimationFrame(() => {
          renderScene();
          setSceneReady(true);
        });
      }
    );

    // Zoom to fit
    const fitDist = calculateZoomToFit(camera, group, rotationRef.current, centerRef.current);
    orbitDistanceRef.current = fitDist;

    renderScene();

    const debugEntry: VisualizerDebugEntry = {
      id: generateVisualizerId('ceiling', `${length}x${width}`),
      kind: 'ceiling',
      renderer,
      scene,
      camera,
      roomGroup: group,
      renderScene,
    };
    debugEntryRef.current = debugEntry;
    registerVisualizer(debugEntry);

    const handleResize = () => {
      if (!container || !cameraRef.current || !rendererRef.current) return;
      cameraRef.current.aspect = container.clientWidth / container.clientHeight;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(container.clientWidth, container.clientHeight);
      renderScene();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(readyTimeout);
      if (lightingEntryRef.current) {
        unregisterLight(lightingEntryRef.current);
        lightingEntryRef.current = null;
      }
      if (debugEntryRef.current) {
        unregisterVisualizer(debugEntryRef.current);
        debugEntryRef.current = null;
      }
      window.removeEventListener('resize', handleResize);
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      if (container && rendererRef.current) container.removeChild(rendererRef.current.domElement);
      rendererRef.current?.dispose();
    };
  }, [length, width, color, boardLayout]);

  // Wheel zoom + touch controls
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const wheelHandler = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      if (!cameraRef.current) return;
      cameraRef.current.fov = applyZoom(cameraRef.current.fov, e.deltaY);
      cameraRef.current.updateProjectionMatrix();
      renderScene();
    };
    container.addEventListener('wheel', wheelHandler, { passive: false });

    const detachTouch = attachTouchControls({
      container,
      getRotation: () => rotationRef.current,
      setRotation: (r) => { rotationRef.current = r; },
      getFov: () => cameraRef.current?.fov ?? 50,
      setFov: (fov) => {
        if (cameraRef.current) {
          cameraRef.current.fov = fov;
          cameraRef.current.updateProjectionMatrix();
        }
      },
      onUpdate: () => renderScene(),
      onDragStart: () => setHasRotated(true),
    });

    return () => {
      container.removeEventListener('wheel', wheelHandler);
      detachTouch();
    };
  }, []);

  const buildCeiling = (group: THREE.Group) => {
    // Dispose old
    group.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry?.dispose();
        if (child.material instanceof THREE.Material) child.material.dispose();
      }
    });
    while (group.children.length > 0) group.remove(group.children[0]);

    // Build board meshes — ceiling is a flat plane at Y=0
    // Boards from layout are in 2D (x=along length, y=along width)
    // Map to XZ plane: board.x -> X, board.y -> Z
    const boards = boardLayout.boards;

    // Group by color
    const boardsByColor = new Map<string, typeof boards>();
    for (const b of boards) {
      if (!boardsByColor.has(b.color)) boardsByColor.set(b.color, []);
      boardsByColor.get(b.color)!.push(b);
    }

    for (const [colorHex, colorBoards] of boardsByColor) {
      const isWhite = colorHex.toUpperCase() === '#FFFFFF';
      const material = createBoardMaterial(isWhite ? 'White' : 'Gray');

      const n = colorBoards.length;
      const positions = new Float32Array(n * 4 * 3);
      const normals = new Float32Array(n * 4 * 3);
      const indices: number[] = [];

      for (let i = 0; i < n; i++) {
        const b = colorBoards[i];
        const off = i * 12;
        const vo = i * 4;
        // Map 2D board to XZ plane at Y=0, normal facing DOWN (-Y)
        // so lighting from below illuminates the visible underside correctly
        positions[off] = b.x;           positions[off+1] = 0; positions[off+2] = b.y;
        positions[off+3] = b.x+b.width; positions[off+4] = 0; positions[off+5] = b.y;
        positions[off+6] = b.x+b.width; positions[off+7] = 0; positions[off+8] = b.y+b.height;
        positions[off+9] = b.x;          positions[off+10] = 0; positions[off+11] = b.y+b.height;
        for (let v = 0; v < 4; v++) {
          normals[off+v*3] = 0; normals[off+v*3+1] = -1; normals[off+v*3+2] = 0;
        }
        indices.push(vo, vo+1, vo+2, vo, vo+2, vo+3);
      }

      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geo.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
      geo.setIndex(indices);
      group.add(new THREE.Mesh(geo, material));
    }

    // Seam lines (matching wall seam style)
    const seamPoints: number[] = [];
    for (const b of boards) {
      const z = -0.001; // Below the surface (underside)
      seamPoints.push(b.x, z, b.y, b.x+b.width, z, b.y);
      seamPoints.push(b.x+b.width, z, b.y, b.x+b.width, z, b.y+b.height);
      seamPoints.push(b.x+b.width, z, b.y+b.height, b.x, z, b.y+b.height);
      seamPoints.push(b.x, z, b.y+b.height, b.x, z, b.y);
    }
    if (seamPoints.length > 0) {
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.Float32BufferAttribute(seamPoints, 3));
      group.add(new THREE.LineSegments(geo, new THREE.LineBasicMaterial({
        color: 0x666666, transparent: true, opacity: 0.5,
      })));
    }

    // Dimension lines (same style as Room3DViewer)
    const LINE_COLOR = 0x0066cc;
    const lineMat = new THREE.LineBasicMaterial({ color: LINE_COLOR, linewidth: 2 });
    const CAP = 0.3;
    const dimOffset = -0.8;

    // Length line along X (front edge, z = -offset)
    const addLine = (start: THREE.Vector3, end: THREE.Vector3) => {
      const geo = new THREE.BufferGeometry().setFromPoints([start, end]);
      group.add(new THREE.Line(geo, lineMat));
    };
    const addCap = (pt: THREE.Vector3, axis: 'x' | 'y' | 'z') => {
      const s = pt.clone(); const e = pt.clone();
      (s as any)[axis] -= CAP; (e as any)[axis] += CAP;
      addLine(s, e);
    };

    // Length along X at front (z = dimOffset)
    addLine(new THREE.Vector3(0, dimOffset, -0.5), new THREE.Vector3(length, dimOffset, -0.5));
    addCap(new THREE.Vector3(0, dimOffset, -0.5), 'y');
    addCap(new THREE.Vector3(length, dimOffset, -0.5), 'y');

    // Width along Z at left (x = dimOffset)
    addLine(new THREE.Vector3(-0.5, dimOffset, 0), new THREE.Vector3(-0.5, dimOffset, width));
    addCap(new THREE.Vector3(-0.5, dimOffset, 0), 'y');
    addCap(new THREE.Vector3(-0.5, dimOffset, width), 'y');

    group.add(createLabelSprite(formatDimension(length), length / 2, dimOffset - 1, -0.5));
    group.add(createLabelSprite(formatDimension(width), -0.5, dimOffset - 1, width / 2));
  };

  const resetView = () => {
    if (isAnimating || !cameraRef.current) return;
    setIsAnimating(true);
    const start = { rotation: { ...rotationRef.current }, fov: cameraRef.current.fov };
    const target = { rotation: { ...CEILING_INITIAL_ROTATION }, fov: DEFAULT_FOV };
    const duration = 800;
    const startTime = Date.now();
    const animate = () => {
      const progress = Math.min((Date.now() - startTime) / duration, 1);
      const state = interpolateState(start, target, progress);
      rotationRef.current = state.rotation;
      if (cameraRef.current) { cameraRef.current.fov = state.fov; cameraRef.current.updateProjectionMatrix(); }
      renderScene();
      if (progress < 1) { animationFrameRef.current = requestAnimationFrame(animate); }
      else { setIsAnimating(false); setHasRotated(false); animationFrameRef.current = null; }
    };
    animate();
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    isDraggingRef.current = true;
    previousMouseRef.current = { x: e.clientX, y: e.clientY };
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDraggingRef.current) return;
    rotationRef.current = applyDragRotation(
      rotationRef.current,
      e.clientX - previousMouseRef.current.x,
      e.clientY - previousMouseRef.current.y
    );
    previousMouseRef.current = { x: e.clientX, y: e.clientY };
    setHasRotated(true);
    renderScene();
  };
  const handleMouseUp = () => { isDraggingRef.current = false; };

  return (
    <div ref={viewerWrapperRef} className={`room-3d-viewer ${compact ? 'compact' : ''} ${isFullscreen ? 'fullscreen' : ''}`}>
      <div className="viewer-dimension-card">
        {`Ceiling: ${formatDimension(length)} L × ${formatDimension(width)} W`}
      </div>
      <div
        ref={containerRef}
        className="viewer-container"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <div ref={spinnerRef} style={{
          position: 'absolute', inset: 0, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          background: '#f9fafb', zIndex: 2, borderRadius: '8px',
          opacity: sceneReady ? 0 : 1,
          pointerEvents: sceneReady ? 'none' : 'auto',
          transition: sceneReady ? 'opacity 0.5s ease-in-out' : 'none',
        }}>
          <div style={{
            width: '32px', height: '32px', border: '3px solid #e5e7eb',
            borderTopColor: '#6b7280', borderRadius: '50%',
            animation: 'viewer-spin 0.8s linear infinite',
          }} />
        </div>
      </div>
      <div className="viewer-hint">{t('viewer.hint')}{isFullscreen ? t('viewer.hintEsc') : ''}</div>
      <div className="viewer-controls">
        <button
          className="btn btn-secondary"
          onClick={resetView}
          disabled={!hasRotated || isAnimating}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px',
            fontSize: 'var(--font-size-xs)', fontWeight: 600,
            cursor: (hasRotated && !isAnimating) ? 'pointer' : 'not-allowed',
            opacity: (hasRotated && !isAnimating) ? 1 : 0.5,
          }}
        >
          <RotateCcw size={14} />
          {t('viewer.resetView')}
        </button>
        {canFullscreen && (
          <button
            className="btn btn-secondary"
            onClick={toggleFullscreen}
            title={isFullscreen ? t('viewer.exitFullScreen') : t('viewer.fullScreen')}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px',
              fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            {isFullscreen ? <Minimize size={14} /> : <Maximize size={14} />}
            {isFullscreen ? t('viewer.exitFullScreen') : t('viewer.fullScreen')}
          </button>
        )}
      </div>
    </div>
  );
};

// Wrapped so a WebGL failure shows a placeholder instead of crashing the wizard.
export const Ceiling3DViewer = withViewerBoundary(Ceiling3DViewerBase);
